import * as React from 'react'
import shortid from 'shortid';
import ImageResize from 'quill-image-resize-module-react';
import { BeatLoader } from 'react-spinners';
import { Row, Label, CustomInput, Button } from 'reactstrap';
import Col from 'reactstrap/lib/Col';
import { useHistory } from 'react-router';
import ReactQuill, { Quill } from 'react-quill';
import { toastr } from 'react-redux-toastr';
import { WrappedInput } from 'src/share/components/Input/Input';
import { UserTypes } from 'src/modules/Auth/store/types';
import { ImageFormat } from './ImageFormat';
import { HelpService } from 'src/share/services/help';
import { IHelpContent, initialHelpArticle } from '../types/help';
import './HelpArticleForm.css';

Quill.register('modules/imageResize', ImageResize);
Quill.register(ImageFormat, true);

export const modules = {
  toolbar: [
    [{ 'header': '1' }, { 'header': '2' }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' },
    { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image']
  ],
  imageResize: {
    parchment: Quill.import('parchment')
  },
};

export const formats = [
  'header', 'size', 'align',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link', 'image', 'width', 'alt', 'height', 'style', 'inline'
];

function HelpArticleForm(props) {
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(false);
  const [article, setArticle] = React.useState<IHelpContent>(initialHelpArticle);
  const [userTypes, setUserTypes] = React.useState(new Set<number>());
  const { articleId, categoryId, categoryName } = props.match.params;
  const isEditing = props.location.state && props.location.state.isEditing;
  React.useEffect(() => {
    if(isEditing) {
      fetchData();
      return;
    }
    initializeArticle();
  }, []);
  async function fetchData(): Promise<void> {
    try {
      setIsLoading(true);
      const newArticle = await HelpService.getArticleById(articleId);
      if (newArticle) {
        setArticle(newArticle)
        setUserTypes(new Set(newArticle.userTypes));
        setIsLoading(false)
      } else {
        history.push('/help');
        toastr.error('There was a problem loading the article');
      }
    } catch {
      toastr.error('Error', 'There was a problem connecting to the server');
      setIsLoading(false);
    }
  }
  function initializeArticle() {
    setArticle(prevArticle => ({ ...prevArticle, articleId: shortid.generate(), categoryId }))
  }
  function handleChecked(e) {
    const key = UserTypes[e.target.name] as any;
    const checked = e.target.checked;
    if(checked) {
      setUserTypes((prevUserTypes) => (new Set(prevUserTypes).add(key)));
    }
    if(!checked && userTypes.has(key)) {
      const newUserTypes = new Set(userTypes);
      newUserTypes.delete(key);
      setUserTypes(newUserTypes);
    }
  }
  function handleInputChanged(key, event) {
    if (event && event.target) {
      const value = event.target.value
      setArticle(prevArticle => ({ ...prevArticle, [key]: value }));
    } else {
      setArticle(prevArticle => ({ ...prevArticle, [key]: event }));
    }
  }
  async function handleArticleSaved() {
    try {
      if (article.content) {
        setIsLoading(true);
        await HelpService.saveContent({ ...article, userTypes: Array.from(userTypes) }, 'article');
        history.push(`/help/article/${categoryName}/${categoryId}/${article.articleId}`);
      } else {
        toastr.error('Error', 'There article content is empty');
      }
    } catch {
      setIsLoading(false);
      toastr.error('Error', 'There was a problem connecting to the server');
    }
  }
  return (
  <div className="help-center__form">
    {!isLoading &&
      <div className={"white-background help-content"}>
        <Row>
          <Col className="col-md-12">
            <div className="article-view-nav nav-article">
              <h2 onClick={() => history.push('/help')}>Support</h2>
              <h2 className="separator">&gt;</h2>
              <h2 onClick={() => history.push(`/help/category/${categoryId}`)}>{categoryName}</h2>
              <h2 className="separator">&gt;</h2>
              <h2>{article.title}</h2>
            </div>
          </Col>
        </Row>
        <Row>
          <Col className="col-md-12 mb-2">
            <WrappedInput 
              placeholder="Title"
              type="text"
              className="help-article-title" 
              name="title"
              value={article.title}
              required={true}
              onChange={(e) => handleInputChanged('title', e)}
            />
          </Col>
        </Row>
        <Row>
        <div className="ml-3">
          <Label for="exampleCheckbox">Select the users who will be able to see this. If none is selected only admin users will have access.</Label>
          <div>
            <CustomInput 
              type="checkbox" 
              label="Case Manager" 
              id="caseManager"
              name="CASEMANAGER"
              checked={userTypes.has(UserTypes.CASEMANAGER)}
              onChange={handleChecked}
            />
            <CustomInput 
              type="checkbox" 
              label="Law Enforcement User" 
              id="lawEnforcementUser"
              name="LAWENFORCEMENTAGENT"
              checked={userTypes.has(UserTypes.LAWENFORCEMENTAGENT)}
              onChange={handleChecked}
            />
          </div>
        </div>
        </Row>
        <Row className="help-quill-row">
          <Col className="col-md-12">
            <ReactQuill
              modules={modules}
              formats={formats}
              value={article.content}
              placeholder="Content"
              onChange={(e) => handleInputChanged('content', e)}
            />
          </Col>
        </Row>
        <Row>
          <Col className="col-md-12">
            <Button className="btn float-right btn-green-fill btn-help" disabled={isLoading} onClick={handleArticleSaved}>
              {isEditing ? 'Update' : 'Save'}
            </Button>
            <Button 
              className="btn float-right btn-grey-fill btn-help" 
              disabled={isLoading ? true : false} 
              onClick={() => history.push(`/help/article/${categoryName}/${categoryId}/${articleId}`)}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </div>}
    <div className="help-loader">
      <BeatLoader loading={isLoading} />
    </div>
  </div>)
}

export default React.memo(HelpArticleForm);
